import { useTranslation } from 'react-i18next';
import { ChevronRight, ShieldOff } from 'lucide-react';
import { PERMISSIONS, type PermissionKey } from '@nexa/auth';
import { permissionGroupLabel, permissionLabel, roleLabel } from '@/lib/access/labels';
import { CATALOG_BY_GROUP, type IGrantingRole } from '@/lib/team/grants';
import { PermissionFlags } from './access-chips';

// "Permisos efectivos" (brief §5 P2 preview, P3 tab, P7): the union of a member's roles,
// read-only, grouped by the 16 §4 groups — each permission with its flags and, where the
// role definitions are known, "otorgado por: <rol>".

export function EffectivePermissions({
  permissions,
  grants,
  compact = false,
  testId = 'effective-permissions',
}: {
  permissions: Iterable<PermissionKey>;
  /** Permission → the roles granting it. Omit when the definitions are not readable. */
  grants?: ReadonlyMap<PermissionKey, readonly IGrantingRole[]>;
  /** Collapsed groups (the invite preview); expanded otherwise. */
  compact?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation('admin');
  const held = new Set(permissions);
  const groups = CATALOG_BY_GROUP.map((g) => ({
    id: g.id,
    items: g.permissions.filter((p) => held.has(p.key)),
  })).filter((g) => g.items.length > 0);

  if (groups.length === 0) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground"
        data-testid={`${testId}-empty`}
      >
        <ShieldOff className="h-4 w-4 shrink-0" aria-hidden />
        {t('users.effective.none')}
      </div>
    );
  }

  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <p className="text-xs text-muted-foreground">
        {t('users.effective.count', { held: total, total: PERMISSIONS.length })}
      </p>
      {groups.map((g) => (
        <details
          key={g.id}
          open={!compact}
          className="group rounded-md border border-border bg-card"
          data-testid={`${testId}-group-${g.id}`}
        >
          <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium">
            <ChevronRight
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
              aria-hidden
            />
            <span className="min-w-0 flex-1">{permissionGroupLabel(t, g.id)}</span>
            <span className="text-xs font-normal text-muted-foreground">{g.items.length}</span>
          </summary>
          <ul className="divide-y border-t">
            {g.items.map((p) => {
              const by = grants?.get(p.key) ?? [];
              return (
                <li
                  key={p.key}
                  className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3"
                  data-testid={`${testId}-item`}
                  data-permission={p.key}
                >
                  <div className="min-w-0">
                    <p className="text-sm">{permissionLabel(t, p.key)}</p>
                    <p className="break-all font-mono text-[11px] text-muted-foreground">{p.key}</p>
                    {by.length > 0 ? (
                      <p className="mt-0.5 text-xs text-muted-foreground" data-testid="granted-by">
                        {t('users.effective.grantedBy', {
                          roles: by.map((r) => roleLabel(t, r)).join(', '),
                        })}
                      </p>
                    ) : null}
                  </div>
                  <PermissionFlags def={p} />
                </li>
              );
            })}
          </ul>
        </details>
      ))}
    </div>
  );
}
